import { ErrorHandler, Inject, Injectable, OnDestroy } from '@angular/core';
import { Device } from '@capacitor/device';
import { EMPTY, Subject, catchError, from, merge, switchMap, takeUntil, timer } from 'rxjs';
import PrinterDesktop from 'src/plugins/printer-desktop';
import { environment } from '../../environments/environment';
import PrinterTerminal from '../../plugins/printer-terminal';
import { SCREEN_LAYOUT, ScreenLayout } from '../core/layout/layout.provider';
import { BackendService } from './backend.service';
import { DataService } from './data.service';
import {
  PrinterStatus,
  PrinterTypes,
  Settings,
} from './models/models';
import { APP_SETTINGS } from './settings-data.provider';
import PrinterAndroid from '../../plugins/printer-android';
import { PrintReceipt as PrintIOS } from '../../plugins/printer-ios';
import { AuthUserService } from '../auth/auth-user.service';

const PRINTER_TYPES_SUPPORT_JOB_CANCELLATION: string[] = [PrinterTypes.EPSON_IHUB];
const PRINTER_TYPES_USE_PDF_FOR_PRINTING: string[] = [PrinterTypes.AIR_PRINT_GENERAL];


@Injectable({ providedIn: 'root' })
export class PrinterService implements OnDestroy {
  printerStatus: PrinterStatus | null = null;

  private destroyed = new Subject<void>();
  private statusReloadtrigger = new Subject<void>();

  /** Only specific printer types support clearing queued jobs */
  get supportQueuedJobsClear(): boolean {
    return PRINTER_TYPES_SUPPORT_JOB_CANCELLATION.includes(this.settings.printerType);
  }

  /** Detects does the printer has jobs to clear and is it online to send clear signal */
  get canClearQueuedJobs(): boolean {
    if (!this.printerStatus) {
      return false;
    }

    return this.printerStatus.printerState === 'online' && this.printerStatus.jobCount > 0;
  }

  /** Indicates when for printing general report data is presented */
  get isGeneralReportPrintEnabled(): boolean {
    if (PRINTER_TYPES_USE_PDF_FOR_PRINTING.includes(this.settings.printerType)) {
      return !!this.dataService.cashBox.reportPdfUrl;
    }

    return !!this.dataService.cashBox.reportEposUrl;
  }

  constructor(@Inject(APP_SETTINGS) private readonly settings: Settings,
    @Inject(SCREEN_LAYOUT) private readonly screenLayout: ScreenLayout,
    private readonly dataService: DataService,
    private readonly backendService: BackendService,
    private readonly error: ErrorHandler,
    private readonly authUserService: AuthUserService
  ) {
    this.initPrinterStatusPolling();
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  async printCashboxSummaryReport(): Promise<void> {
    if (this.dataService.cashBox.status !== 'closed') {
      throw new Error('Printing summary report should be enabled only after cashbox is closed.');
    }

    if (!this.isGeneralReportPrintEnabled) {
      throw new Error('Printing summary report has no data to print.');
    }

    return this.sendToPrinter({
      pdfUrl: this.dataService.cashBox.reportPdfUrl as string, // Already validated by 'isGeneralReportPrintEnabled'
      eposUrl: this.dataService.cashBox.reportEposUrl as string, // Already validated by 'isGeneralReportPrintEnabled'
      imageUrl: this.dataService.cashBox.reportImageUrl as string, // Already validated by 'isGeneralReportPrintEnabled'
    });
  }

  async printReceipt(
    receiptUrl?: string,
    receiptImageUrl?: string,
    receiptEposUrl?: string,
    isMerchant = false
  ): Promise<void> {
    if (!receiptUrl || !receiptImageUrl || !receiptEposUrl) {
      throw new Error('At this point this should be not callable');
    }
    const merchantUrl = `${receiptEposUrl}&merchant_receipt=true`;
    return this.sendToPrinter({ 
      pdfUrl: receiptUrl ?? '',
      eposUrl: isMerchant ? merchantUrl : (receiptEposUrl ?? ''),
      imageUrl: receiptImageUrl ?? '',
    });
  }

  async cancelQueuedJobs() {
    if (!this.supportQueuedJobsClear) {
      throw new Error(`The printer type: "${this.settings.printerType}" not supports cancellation of queued jobs.`);
    }

    await this.backendService.cancelQueuedPrinterJobs(this.settings.epsonIhubDeviceId!);
    return this.statusReloadtrigger.next();
  }

  private async printOnTerminal(imageUrl: string) {
    try {
      const url = new URL(imageUrl);

      if (environment.addTseToInvoice) {
        // #2yyxbf5 requested to add the TSE info to image based invoice preview
        url.searchParams.append('template', 'ksv_receipt');
        url.searchParams.append('v', '2');
      }

      return await PrinterTerminal.print({ imageUrl: url.toString() });
    } catch (error) {
      this.error.handleError(error);
    }
  }

  private async printOnDesktopDevice(imageUrl: string) {
    try {
      return await PrinterDesktop.print({ imageUrl: imageUrl });
    } catch (error) {
      this.error.handleError(error);
    }
  }

  private async printAndroid(pdfUrl: string) {
    try {
      return await PrinterAndroid.print({ pdfUrl: pdfUrl });
    } catch (error) {
      this.error.handleError(error);
    }
  }

  private async printIOS(url: string) {
    try {
      return await PrintIOS.print({ url: url });
    } catch (error) {
      this.error.handleError(error);
    }
  }

  /** Sends the print request to external printer or allows printing locally, based on app configuration */
  private async sendToPrinter(printPayload: { pdfUrl: string; eposUrl: string; imageUrl: string }): Promise<void> {
    if (environment.runOnAndroidPaymentTerminal) {
      this.printOnTerminal(printPayload.imageUrl);
      return;
    }

    const deviceInfo = await Device.getInfo();
    if (deviceInfo.manufacturer === 'SUNMI' && deviceInfo.platform === 'android') {
      this.printOnDesktopDevice(printPayload.imageUrl);
      return;
    }

    const externalPrinterPrintPayload = {
      device_id: this.settings.posUuid,
      receipt_url: printPayload.eposUrl
    };

    switch (this.settings.printerType) {
      case PrinterTypes.EPSON_IHUB:
        await this.backendService.callPrintEpsonIhub(externalPrinterPrintPayload);
        break;
      case PrinterTypes.PRINTERBOX:
        await this.backendService.callPrintPrinterbox(externalPrinterPrintPayload);
        break;
      case PrinterTypes.AIR_PRINT_GENERAL:
        if (this.screenLayout === ScreenLayout.desktop) {
          window.open(printPayload.pdfUrl, '_blank');
        }
        else if (deviceInfo.platform === 'ios') {
          await this.printIOS(printPayload.pdfUrl);
        }
        else {
          await this.printAndroid(printPayload.pdfUrl);
        }
        break;
      default:
        const error = new Error($localize`Nicht unterstützte Druckerkonfiguration.`);
        this.error.handleError(error);
    }
  }

  private initPrinterStatusPolling() {
    // System printer (AIR_PRINT_GENERAL) doesn't need status polling
    // as it only opens system print dialog and is always "connected"
    if (this.settings.printerType === PrinterTypes.AIR_PRINT_GENERAL) {
      this.printerStatus = {
        id: '',
        status: 'online',
        printerState: 'online',
        jobCount: 0
      };
      return;
    }

    const STATUS_POLL_INTERVAL_MS = 10000;

    const printerRequest = () => from(this.backendService.fetchPrinterStatus(this.settings.posUuid)).pipe(
      catchError(error => {
        if (error.status === 0) {
          // Sometime we get this error when the app is offline, we should ignore it regarding polling
          return EMPTY;
        }

        this.error.handleError(error);
        return EMPTY;
      })
    );

    this.authUserService.printerStatusSubscription = merge(timer(0, environment.pollingDelay || STATUS_POLL_INTERVAL_MS), this.statusReloadtrigger).pipe(
      switchMap(printerRequest),
      takeUntil(this.destroyed)
    ).subscribe(status => {
      this.printerStatus = status;
    });
  }
}
